import { Button, ImportIcon, Table, type TableColumns } from '@bp/ui-components';
import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from '../../../utils/dateCalculations';
import type { ProcuratGroup } from '../hooks/useProcuratGroups';

type ProcuratGroupsTableDataType = ProcuratGroup & { modificationDate: string };
type ProcuratClassesTableProps = {
  data: ProcuratGroupsTableDataType[];
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onImport: () => void;
};

export const ProcuratGroupsTable = ({
  data, // procurat Gruppen
  rowSelection,
  onRowSelectionChange,
  onImport,
}: ProcuratClassesTableProps) => {
  const { t } = useTranslation();

  const groupColumns: TableColumns<ProcuratGroupsTableDataType>[] = useMemo(() => {
    return [
      {
        header: t('common.name'),
        id: 'name',
        accessorKey: 'name',
        canExpand: true,
      },
      {
        id: 'lastImport',
        accessorKey: 'lastImport',
        header: t('common.lastUpdated'),
        cell: (cell) => {
          return niceDate(cell.row.original.modificationDate, 'short', 'short');
        },
        size: 150,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('common.imported'),
        accessorFn: (item) => !!item.modificationDate, // if it has a date, it is already imported
        type: 'boolean',
        size: 150,
      },
    ];
  }, [t]);

  return (
    <>
      <Table<ProcuratGroupsTableDataType>
        showActionBar
        actionBarSettings={{
          showSelectedRowsCount: true,
          showExpertFilter: false,
          globalFilterPlaceholder: t('common.name'),
          extendedActionsRight: (
            <Button
              hierarchy='tertiary'
              icon={<ImportIcon />}
              onClick={onImport}
              disabled={Object.keys(rowSelection).length < 1}
            >
              {t('procuratImport.importGroups')}
            </Button>
          ),
        }}
        columns={groupColumns}
        data={data}
        onRowSelectionChange={onRowSelectionChange}
        rowSelection={rowSelection}
        showSelect
        customPadding='var(--spacing-7)'
      />
    </>
  );
};
